import {
  CalendarDaysIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import ProductUpdateAnnouncementModal from "./ProductUpdateAnnouncementModal";

interface StudioClosuresAnnouncementModalProps {
  enabled: boolean;
  studioId?: string | null;
  viewerId?: string | null;
  forceOpen?: boolean;
  onDismiss?: () => void;
}

export const STUDIO_CLOSURES_UPDATE = {
  announcementId: "studio-closure-dates",
  version: "v1",
  publishedLabel: "October 2026",
  title: "Studio closure dates",
  description:
    "Add the days your studio is closed, and leads booking a trial class from your website won't be offered classes on those dates.",
} as const;

export default function StudioClosuresAnnouncementModal({
  enabled,
  studioId = null,
  viewerId = null,
  forceOpen = false,
  onDismiss,
}: StudioClosuresAnnouncementModalProps) {
  const navigate = useNavigate();

  return (
    <ProductUpdateAnnouncementModal
      enabled={enabled}
      announcementId={STUDIO_CLOSURES_UPDATE.announcementId}
      version={STUDIO_CLOSURES_UPDATE.version}
      audienceScope={studioId && viewerId ? [studioId, viewerId] : null}
      forceOpen={forceOpen}
      onDismiss={onDismiss}
      title={STUDIO_CLOSURES_UPDATE.title}
      description={STUDIO_CLOSURES_UPDATE.description}
      icon={<CalendarDaysIcon className="h-7 w-7" aria-hidden="true" />}
      closeLabel="Close studio closure dates announcement"
      primaryAction={{
        label: "Go to Settings",
        onClick: () => navigate("/app/settings/general"),
      }}
    >
      <section>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">
          What it does
        </h3>
        <div className="mt-3 space-y-3 text-sm leading-6 text-gray-600 dark:text-gray-300">
          <div className="flex gap-3">
            <CheckCircleIcon
              className="mt-0.5 h-5 w-5 flex-none text-green-600 dark:text-green-400"
              aria-hidden="true"
            />
            <p>
              When a lead books a trial class from your website, they only see
              dates your studio is open. Closed days are left out, so nobody shows
              up to a locked door.
            </p>
          </div>
          <div className="flex gap-3">
            <CheckCircleIcon
              className="mt-0.5 h-5 w-5 flex-none text-green-600 dark:text-green-400"
              aria-hidden="true"
            />
            <p>
              A closure covers the whole day, for every class on that date.
            </p>
          </div>
          <div className="flex gap-3">
            <CheckCircleIcon
              className="mt-0.5 h-5 w-5 flex-none text-green-600 dark:text-green-400"
              aria-hidden="true"
            />
            <p>
              Your lead forms, automatic texts, and follow-ups work the same as
              before.
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-indigo-200 bg-indigo-50/70 p-5 dark:border-indigo-800 dark:bg-indigo-950/30">
        <h3 className="text-base font-semibold text-indigo-950 dark:text-indigo-100">
          How to add a closure
        </h3>
        <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm leading-6 text-indigo-950/80 marker:font-bold marker:text-indigo-600 dark:text-indigo-100/80 dark:marker:text-indigo-300">
          <li className="pl-1">
            Go to <span className="font-semibold">Settings → General</span>.
          </li>
          <li className="pl-1">
            In <span className="font-semibold">Studio Closures</span>, pick the
            date and click <span className="font-semibold">Add Closure</span>.
          </li>
          <li className="pl-1">
            To reopen a day, click the <span className="font-semibold">×</span>{" "}
            next to it.
          </li>
        </ol>
        <p className="mt-4 border-t border-indigo-200 pt-3 text-xs leading-5 text-indigo-800 dark:border-indigo-800 dark:text-indigo-200">
          Trial classes already booked for a day you close aren&apos;t cancelled
          automatically. Check your appointments for that day and reach out to
          those leads.
        </p>
      </section>
    </ProductUpdateAnnouncementModal>
  );
}
